import {
  Component,
  Output,
  EventEmitter,
  HostBinding,
  Input,
} from '@angular/core';
import { SettingsService } from '../services/settings/settings.service';
import { NovoFormGroup } from 'novo-elements';
import { SearchService } from '../services/search/search.service';
import { Router } from '@angular/router';
import { IAdditionalLanguageOption } from '../typings/settings';
import { RadiusZipService } from '../services/radius-zip/radius-zip.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent {
  @Output() public newFilter: EventEmitter<any> = new EventEmitter();
  @Output() public toggleSidebar: EventEmitter<boolean> = new EventEmitter();
  @HostBinding('class.active') @Input() public display: boolean = false;

  public filterUrl: any;

  public controls: any[] = [];
  public updateFilterOptions: Function;
  public sidebarForm: NovoFormGroup;
  public keyword: string = '';
  public timeout: any;
  public loading: boolean = false;
  public filter: any = {};

  public zipCode: string = '';
  public radiusMiles: number = 25;

  public showPrivacyPolicy: boolean =
    SettingsService.settings.privacyConsent.sidebarLink;

  public languageDropdownEnabled =
    SettingsService.settings.languageDropdownOptions?.enabled &&
    !SettingsService.isServer;

  public availableLocales: IAdditionalLanguageOption[] =
    SettingsService.settings?.languageDropdownOptions?.choices || [];

  constructor(
    private searchService: SearchService,
    private radiusZipService: RadiusZipService,
    private router: Router,
  ) {}

  public searchOnDelay(): void {
    const keywordSearchFields: string[] =
      SettingsService.settings.service.keywordSearchFields;

    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    this.timeout = setTimeout(() => {
      let searchString: string = '';

      if (this.keyword.trim()) {
        keywordSearchFields.forEach((field: string, index: number) => {
          if (index > 0) {
            searchString += ' OR ';
          }

          searchString += `${field}{?^^equals}${
            this.keyword.trim() ? this.keyword.trim() + '*' : ''
          }`;
        });
      }

      delete this.filter['ids'];

      if (searchString) {
        this.filter['keyword'] = searchString;
      } else {
        delete this.filter['keyword'];
      }

      this.searchWithOptionalZipRadius();
    }, 250);
  }

  public searchZipRadius(): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    this.timeout = setTimeout(() => {
      delete this.filter['ids'];
      this.searchWithOptionalZipRadius();
    }, 250);
  }

  public updateFilter(
    field: string,
    httpFormatedFilter: string | string[],
  ): void {
    delete this.filter['keyword'];

    this.filter[field] = httpFormatedFilter;

    const filter: any = {};
    Object.assign(filter, this.filter);

    this.filter = filter; // triggering angular change detection
    this.newFilter.emit(this.filter);
  }

  public hideSidebar(): void {
    this.toggleSidebar.emit(false);
  }

  public viewPrivacyPolicy(): void {
    const url: string =
      SettingsService.settings.privacyConsent.privacyPolicyUrl;

    if (url === '/privacy') {
      this.router.navigate([url]);
    } else {
      window.open(url);
    }
  }

  public setPreferredLanguage(language: string): void {
    localStorage.setItem('preferredLanguage', language);
    location.reload();
  }

  private searchWithOptionalZipRadius(): void {
    const zip: string = this.zipCode ? this.zipCode.trim() : '';

    this.loading = true;

    if (!zip) {
      delete this.filter['zipRadius'];

      this.searchService
        .getCurrentJobIds(this.filter, [])
        .subscribe({
          next: this.handleJobIdsOnSuccess.bind(this),
          error: (error: any) => {
            console.error('Bullhorn search failed without ZIP:', error);
            this.loading = false;
          },
        });

      return;
    }

    this.radiusZipService.getZipCodes(zip, this.radiusMiles).subscribe({
      next: (zipCodes: string[]) => {
        console.log('RadiusZip returned ZIP codes:', zipCodes);

        const zipFilters: string[] = this.buildZipFilters(zipCodes);

        console.log('Bullhorn ZIP filter values:', zipFilters);

        if (zipFilters.length > 0) {
          this.filter['zipRadius'] = zipFilters;
        } else {
          delete this.filter['zipRadius'];
        }

        this.searchService
          .getCurrentJobIds(this.filter, [])
          .subscribe({
            next: this.handleJobIdsOnSuccess.bind(this),
            error: (error: any) => {
              console.error('Bullhorn search failed after RadiusZip:', error);
              this.loading = false;
            },
          });
      },
      error: (error: any) => {
        console.error('RadiusZip API failed:', error);

        const fallbackZipFilters: string[] = this.buildZipFilters([zip]);

        if (fallbackZipFilters.length > 0) {
          this.filter['zipRadius'] = fallbackZipFilters;
        } else {
          delete this.filter['zipRadius'];
        }

        this.searchService
          .getCurrentJobIds(this.filter, [])
          .subscribe({
            next: this.handleJobIdsOnSuccess.bind(this),
            error: (bullhornError: any) => {
              console.error('Bullhorn fallback search failed:', bullhornError);
              this.loading = false;
            },
          });
      },
    });
  }

  private buildZipFilters(zipCodes: string[]): string[] {
  if (!zipCodes || zipCodes.length === 0) {
    return [];
  }

  return zipCodes.map((zip: string) => {
    return `address.zip:${zip}`;
  });
}

  private handleJobIdsOnSuccess(res: any): void {
    console.log('Bullhorn result:', res);

    this.loading = false;

    const resultIds: string[] = res.data.map((result: any) => {
      return `id{?^^equals}${result.id}`;
    });

    if (resultIds.length === 0) {
      resultIds.push(`id{?^^equals}${0}`);
    }

    this.updateFilter('ids', resultIds);
  }
}